import { useEffect, useState } from 'react';
import { getPosts } from '../lib/api-client';
import FeedClient from '../components/desktop/FeedClient';
import LoadingScreen from '../components/desktop/LoadingScreen';
import LoginOverlay from '../components/desktop/LoginOverlay';
import { useAuth } from '../context/AuthContext';
import resolveAvatar from '../lib/resolveAvatar';

export default function Feed() {
  const { user: authUser, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<any[]>([]);
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      loadData();
    }
  }, [authLoading]);

  async function loadData() {
    setLoading(true);
    try {
      // Load posts
      const postsData = await getPosts();
      const transformedPosts = postsData.map((post: any) => {
          // Transform replies if they exist
          const replies = post.replies?.map((comment: any) => ({
            ...comment,
            likes: comment.likes,
            dislikes: comment.dislikes,
          }));

          const transformed = {
            id: post.id?.toString() || String(Math.random()),
            author: post.author?.name || post.author?.username || 'Unknown',
            authorId: post.author?.id?.toString() || 'unknown',
            authorUsername: post.author?.username || undefined,
            authorAvatar: resolveAvatar(post.author?.profilePicture || post.author?.image),
            title: post.title || undefined,
            slug: post.slug || undefined,
            content: post.content || '',
            tags: Array.isArray(post.tags) ? post.tags : [],
            likes: post.likes,
            dislikes: post.dislikes,
            comments: post._count?.comments || post.comments || 0,
            shares: post._count?.reposts || post.shares || 0,
            timestamp: post.createdAt ? formatTimeAgo(new Date(post.createdAt)) : 'Unknown',
            liked: post.liked || false,
            disliked: post.disliked || false,
            createdAt: post.createdAt || new Date().toISOString(),
            replies: replies,
            // IMPORTANT: Include routing fields for community/forum posts
            postType: post.postType || undefined,
            communitySlug: post.communitySlug || undefined,
            communityName: post.communityName || undefined,
            communityId: post.communityId || undefined,
          };

          // Log any problematic values
          Object.entries(transformed).forEach(([key, value]) => {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
            }
          });

          return transformed;
        });

      setPosts(transformedPosts);
    } catch (error) {
      // User not authenticated, load public posts only
      setShowLoginOverlay(true);

      try {
        const postsData = await getPosts();
        const transformedPosts = postsData.map((post: any) => {
          const replies = post.replies?.map((comment: any) => ({
            ...comment,
            likes: comment.likes,
            dislikes: comment.dislikes,
          }));

          const transformed = {
            id: post.id?.toString() || String(Math.random()),
            author: post.author?.name || post.author?.username || 'Unknown',
            authorId: post.author?.id?.toString() || 'unknown',
            authorUsername: post.author?.username || undefined,
            authorAvatar: resolveAvatar(post.author?.profilePicture || post.author?.image),
            title: post.title || undefined,
            slug: post.slug || undefined,
            content: post.content || '',
            tags: Array.isArray(post.tags) ? post.tags : [],
            likes: post.likes,
            dislikes: post.dislikes,
            comments: post._count?.comments || post.comments || 0,
            shares: post._count?.reposts || post.shares || 0,
            timestamp: post.createdAt ? formatTimeAgo(new Date(post.createdAt)) : 'Unknown',
            liked: post.liked || false,
            disliked: post.disliked || false,
            createdAt: post.createdAt || new Date().toISOString(),
            replies: replies,
            // IMPORTANT: Include routing fields for community/forum posts
            postType: post.postType || undefined,
            communitySlug: post.communitySlug || undefined,
            communityName: post.communityName || undefined,
            communityId: post.communityId || undefined,
          };

          return transformed;
        });
        setPosts(transformedPosts);
      } catch (postsError) {
      }
    } finally {
      setLoading(false);
    }
  }

  function handleLoginSuccess() {
    setShowLoginOverlay(false);
    // Reload data after successful login
    loadData();
  }

  function handleCloseOverlay() {
    setShowLoginOverlay(false);
  }

  if (loading) {
    return <LoadingScreen />;
  }

  // Transform authUser to match expected format
  const currentUser = authUser ? {
    id: authUser.id.toString(),
    username: authUser.username,
    displayName: authUser.displayName || authUser.username,
    avatar: resolveAvatar(authUser.profilePicture),
    isSiteAdmin: authUser.isSiteAdmin || false,
    isSiteModerator: authUser.isSiteModerator || false,
    isSuperAdmin: authUser.isSuperAdmin || false
  } : null;

  return (
    <>
      <FeedClient
        currentUser={currentUser}
        initialPosts={posts}
        isAuthenticated={!!authUser}
      />
      <LoginOverlay
        isOpen={showLoginOverlay}
        onClose={handleCloseOverlay}
        onSuccess={handleLoginSuccess}
      />
    </>
  );
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return 'now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) return `${diffInWeeks}w ago`;

  return date.toLocaleDateString();
}
